import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type FirebaseUser } from './firebase';

export interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  isFirebaseAvailable: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  isFirebaseAvailable?: boolean;
  // Runs synchronously, before the new user reaches React state, whenever the signed-in uid changes
  // (sign-out, expiry, another tab, account switch), so the host can drop the previous person's
  // state before anything renders with it.
  onUserChange?: (previous: FirebaseUser | null, next: FirebaseUser | null) => void;
}

export function AuthProvider({
  children,
  isFirebaseAvailable = true,
  onUserChange,
}: AuthProviderProps): ReactElement {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(isFirebaseAvailable);
  const currentUserRef = useRef<FirebaseUser | null>(null);
  const onUserChangeRef = useRef(onUserChange);

  useEffect(() => {
    onUserChangeRef.current = onUserChange;
  }, [onUserChange]);

  useEffect(() => {
    if (!isFirebaseAvailable) {
      setIsLoading(false);
      setFirebaseUser(null);
      return undefined;
    }
    const unsubscribe = onAuthStateChanged((user) => {
      const previous = currentUserRef.current;
      if ((previous?.uid ?? null) !== (user?.uid ?? null)) {
        onUserChangeRef.current?.(previous, user);
      }
      currentUserRef.current = user;
      setFirebaseUser(user);
      setIsLoading(false);
    });
    return unsubscribe;
  }, [isFirebaseAvailable]);

  return (
    <AuthContext.Provider value={{ firebaseUser, isLoading, isFirebaseAvailable }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuthContext must be used within <AuthProvider>.');
  }
  return ctx;
}
